// ===================================================================
// PA GRANTS — Pipeline Board (Kanban)
// Drag-free visual board of projects grouped by phase
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import { usePolling } from '../../hooks/usePolling';
import { useNavigate } from 'react-router-dom';
import {
  Paper, Text, Group, Stack, Badge, Button, SimpleGrid, Loader, Center, Menu,
} from '@mantine/core';
import { IconFilter, IconChevronDown, IconX, IconArrowLeft } from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { useAuthStore } from '../../store/authStore';
import { DAMAGE_CATEGORY_COLORS, DAMAGE_CATEGORY_LABELS, type DamageCategory } from '../../types/pagrants';

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });

const PHASE_COLUMNS = [
  { key: 'formulation', label: 'Formulation', color: '#339af0' },
  { key: 'review', label: 'In Review', color: '#fcc419' },
  { key: 'obligation', label: 'Obligated', color: '#099268' },
  { key: 'work_in_progress', label: 'Work in Progress', color: '#f76707' },
  { key: 'closeout', label: 'Closeout', color: '#7c3aed' },
] as const;

const CATEGORIES: DamageCategory[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G'];

export default function PAKanban() {
  const navigate = useNavigate();
  const {
    organizations, disasters, projects,
    loadingProjects, fetchAll,
  } = usePAGrantsStore();

  const activeIncident = useAuthStore(s => s.activeIncident);
  const [selectedDisasterId, setSelectedDisasterId] = useState<number | null>(null);
  const [filterOrg, setFilterOrg] = useState<string | null>(null);
  const [filterCat, setFilterCat] = useState<DamageCategory | null>(null);

  useEffect(() => { fetchAll(); }, [fetchAll]);
  usePolling(fetchAll);

  useEffect(() => {
    if (disasters.length === 0) return;
    if (activeIncident) {
      const linked = disasters.find(d => String(d.incidentId) === String(activeIncident.id));
      if (linked) { setSelectedDisasterId(linked.Rid); return; }
    }
    if (selectedDisasterId === null) {
      const active = disasters.find(d => d.status === 'active');
      setSelectedDisasterId(active ? active.Rid : disasters[0].Rid);
    }
  }, [disasters, activeIncident]);

  const selectedDisaster = useMemo(
    () => disasters.find(d => d.Rid === selectedDisasterId) ?? null,
    [disasters, selectedDisasterId],
  );

  const disasterProjects = useMemo(
    () => selectedDisaster ? projects.filter(p => p.disasterId === String(selectedDisaster.Rid) && p.workType !== 'hmgp') : [],
    [projects, selectedDisaster],
  );

  const orgMap = useMemo(() => {
    const m = new Map<string, string>();
    organizations.forEach(o => { m.set(String(o.Rid), o.orgName); });
    return m;
  }, [organizations]);

  const filteredProjects = useMemo(() => {
    let fp = disasterProjects;
    if (filterOrg) fp = fp.filter(p => p.organizationId === filterOrg);
    if (filterCat) fp = fp.filter(p => p.category === filterCat);
    return fp;
  }, [disasterProjects, filterOrg, filterCat]);

  const kanbanColumns = useMemo(() =>
    PHASE_COLUMNS.map(col => ({
      ...col,
      projects: filteredProjects.filter(p => p.phase === col.key),
    })),
    [filteredProjects],
  );

  const orgOptions = useMemo(() => {
    const ids = new Set(disasterProjects.map(p => p.organizationId));
    return organizations.filter(o => ids.has(String(o.Rid)));
  }, [disasterProjects, organizations]);

  if (loadingProjects && projects.length === 0) {
    return <Center h={300}><Loader size="lg" /></Center>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="xs">
          <Button variant="subtle" size="sm" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/pagrants/pa')}>
            Dashboard
          </Button>
          <Text size="lg" fw={700}>Pipeline Board</Text>
          {selectedDisaster && (
            <Badge variant="light" color="blue">{selectedDisaster.disasterName}</Badge>
          )}
        </Group>
        <Group gap="xs">
          {(filterOrg || filterCat) && (
            <Button variant="subtle" size="xs" color="red" leftSection={<IconX size={14} />}
              onClick={() => { setFilterOrg(null); setFilterCat(null); }}>
              Clear
            </Button>
          )}
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button variant={filterOrg ? 'filled' : 'light'} size="xs" leftSection={<IconFilter size={14} />}>
                {filterOrg ? orgMap.get(filterOrg) || 'Applicant' : 'Applicant'}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => setFilterOrg(null)} fw={!filterOrg ? 700 : 400}>All Applicants</Menu.Item>
              <Menu.Divider />
              {orgOptions.map(o => (
                <Menu.Item key={o.Rid} onClick={() => setFilterOrg(String(o.Rid))}
                  fw={filterOrg === String(o.Rid) ? 700 : 400}>
                  {o.orgName}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          <Menu position="bottom-end" withinPortal>
            <Menu.Target>
              <Button variant={filterCat ? 'filled' : 'light'} size="xs" leftSection={<IconFilter size={14} />}>
                {filterCat ? `Cat ${filterCat}` : 'Category'}
              </Button>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => setFilterCat(null)} fw={!filterCat ? 700 : 400}>All Categories</Menu.Item>
              <Menu.Divider />
              {CATEGORIES.map(cat => (
                <Menu.Item key={cat} onClick={() => setFilterCat(cat)}
                  fw={filterCat === cat ? 700 : 400}
                  leftSection={<Badge size="xs" style={{ background: DAMAGE_CATEGORY_COLORS[cat], color: '#fff' }}>{cat}</Badge>}>
                  {DAMAGE_CATEGORY_LABELS[cat]}
                </Menu.Item>
              ))}
            </Menu.Dropdown>
          </Menu>
          {disasters.length > 1 && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <Button variant="light" size="xs" rightSection={<IconChevronDown size={14} />}>Disaster</Button>
              </Menu.Target>
              <Menu.Dropdown>
                {disasters.map(d => (
                  <Menu.Item key={d.Rid} onClick={() => setSelectedDisasterId(d.Rid)} fw={d.Rid === selectedDisasterId ? 700 : 400}>
                    {d.disasterName || `#${d.disasterNumber}`}
                  </Menu.Item>
                ))}
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>
      </Group>

      <SimpleGrid cols={{ base: 1, sm: 2, md: 5 }}>
        {kanbanColumns.map(col => (
          <Paper key={col.key} p="sm" radius="md" style={{ background: '#f8f9fa', minHeight: 200 }}>
            <Group justify="space-between" mb="sm" pb="xs" style={{ borderBottom: `2px solid ${col.color}` }}>
              <Text size="xs" fw={700} tt="uppercase" c={col.color}>{col.label}</Text>
              <Badge size="sm" variant="light">{col.projects.length}</Badge>
            </Group>
            {col.projects.map(p => {
              const orgName = orgMap.get(p.organizationId) || 'Unknown Org';
              const catColor = DAMAGE_CATEGORY_COLORS[p.category as DamageCategory] || '#868e96';
              return (
                <Paper
                  key={p.Rid} p="xs" radius="sm" withBorder mb="xs"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/pagrants/pa/applicants/${p.organizationId}/projects/${p.Rid}`)}
                >
                  <Text size="sm" fw={600}>{p.projectNumber} {p.projectTitle}</Text>
                  <Text size="xs" c="dimmed">{orgName}</Text>
                  <Group justify="space-between" mt="xs">
                    <Badge size="xs" style={{ background: catColor, color: '#fff' }}>{p.category || '—'}</Badge>
                    <Text size="xs" fw={600}>{fmt(parseFloat(p.estimatedCost) || 0)}</Text>
                  </Group>
                </Paper>
              );
            })}
            {col.projects.length === 0 && (
              <Text size="xs" c="dimmed" ta="center" mt="md">No projects</Text>
            )}
          </Paper>
        ))}
      </SimpleGrid>

      <Text size="xs" c="dimmed" ta="center">
        Showing {filteredProjects.length} of {disasterProjects.length} projects
      </Text>
    </Stack>
  );
}
